import { Col, DatePicker, Input, Row, Select, Spin } from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useCallback, useRef, useState } from "react";
import { IoMdClose, IoMdDownload } from "react-icons/io";
import { IoSearchOutline } from "react-icons/io5";
import { RiDeleteBin6Line } from "react-icons/ri";
import apiFactory from "../../api";
import { GeneralModal } from "../../components/modal/GeneralModal";
import StorageFile from "../../components/preview/StorageFile";
import { STORAGE } from "../../config/Constant";
import { useStorageContext } from "../../context/StorageContext";
import { useInfoUser } from "../../store/UserStore";
import { useGlobalDownloader } from '../../context/global/DownloadProvider';
dayjs.extend(utc);
const { RangePicker } = DatePicker;

const locationTypeList = [
  {
    value: "CONVERSATION",
    label: "Chat",
  },
  {
    value: "NOTIFICATION",
    label: "Mail",
  },
  // {
  //   value: "PROJECT",
  //   label: "Project",
  // },
];

const dateTypeList = [
  {
    value: "DATE",
    label: "Date",
  },
  {
    value: "RANGE",
    label: "Range",
  },
];

const StorageList = () => {
  const { resourceList, initResourceList } = useStorageContext();

  // 1. Dùng useGlobalDownloader
  const { startDownloadCompress } = useGlobalDownloader();
  const { languageMap } = useInfoUser();

  const [isOpenModalDelete, setIsOpenModalDelete] = useState(false);
  const [pickedList, setPickedList] = useState([]);
  const [isDownloadingSelected, setIsDownloadingSelected] = useState(false);

  const pickFile = (resourceAccessId) => {
    if (!resourceAccessId) return;

    if (pickedList?.includes(resourceAccessId)) {
      setPickedList(pickedList.filter((id) => id !== resourceAccessId));
    } else {
      setPickedList([...pickedList, resourceAccessId]);
    }
  };

  const deleteStorageFiles = async () => {
    const result = await apiFactory.resourceApi.deleteStorage({
      resourceAccessIds: pickedList,
    });

    if (result?.status !== 200) {
      return;
    }

    setPickedList([]);
    await initResourceList();
  };

  const handleDownload = async (e) => {
    e.stopPropagation();
    if (isDownloadingSelected) return;

    const downloadResourceIds = resourceList
      .filter((resource) => pickedList?.includes(resource?.resourceAccessId))
      .map((resource) => resource?.resourceId)
      .filter(Boolean);

    if (downloadResourceIds.length === 0) return;

    setIsDownloadingSelected(true);
    try {
      await startDownloadCompress(downloadResourceIds, "willo.zip");
    } finally {
      setIsDownloadingSelected(false);
    }
  };

  return (
    <div className="w-[100%] px-[25px]">
      {/* Floating Action Bar (Fixed, zero layout shift) */}
      <div
        className={`fixed bottom-8 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-white/95 backdrop-blur-md px-4 py-2 rounded-full shadow-2xl border border-gray-200 text-gray-800 transition-all duration-300 ${
          pickedList?.length > 0
            ? "opacity-100 translate-y-0 pointer-events-auto"
            : "opacity-0 translate-y-6 pointer-events-none"
        }`}
      >
        <button
          onClick={() => setPickedList([])}
          className="p-1 hover:bg-gray-100 rounded-full transition text-gray-400 hover:text-gray-600 flex items-center cursor-pointer"
          title="Deselect all"
        >
          <IoMdClose className="text-[17px]" />
        </button>
        <div className="text-sm font-semibold text-gray-700 whitespace-nowrap pr-1">
          {pickedList?.length}{" "}
          {languageMap?.["storage.selected"] ?? "Selected"}
        </div>
        <div className="h-4 w-px bg-gray-200" />
        <button
          onClick={() => setIsOpenModalDelete(true)}
          className="flex items-center gap-1.5 px-3.5 py-1.5 bg-red-500 hover:bg-red-600 text-white text-xs font-semibold rounded-full shadow-sm transition-all duration-150 cursor-pointer active:scale-95"
          title={languageMap?.["storage.delete"] ?? "Delete"}
        >
          <RiDeleteBin6Line className="text-[15px]" />
          <span>{languageMap?.["storage.delete"] ?? "Delete"}</span>
        </button>
        <button
          onClick={handleDownload}
          disabled={isDownloadingSelected}
          className={`flex items-center gap-1.5 px-3.5 py-1.5 text-white text-xs font-semibold rounded-full shadow-sm transition-all duration-150 ${
            isDownloadingSelected
              ? "bg-[#237d44] opacity-80 cursor-wait"
              : "bg-[#2b9853] hover:bg-[#237d44] cursor-pointer active:scale-95"
          }`}
          title={
            isDownloadingSelected
              ? "Preparing download..."
              : languageMap?.["storage.download"] ?? "Download"
          }
        >
          {isDownloadingSelected ? (
            <Spin size="small" />
          ) : (
            <IoMdDownload className="text-[15px]" />
          )}
          <span>
            {isDownloadingSelected
              ? "Preparing download..."
              : languageMap?.["storage.download"] ?? "Download"}
          </span>
        </button>
      </div>

      <Row gutter={[16, 16]}>
        {resourceList?.map((item, index) => (
          <Col
            key={item?.resourceAccessId || item?.resourceId || item?.uid || index}
            xs={24}
            sm={12}
            md={8}
            lg={6}
            xl={6}
            xxl={4}
            className="flex justify-center"
          >
            <StorageFile
              file={item}
              pickFile={pickFile}
              pickedList={pickedList}
              setPickedList={setPickedList}
            />
          </Col>
        ))}
      </Row>
      {isOpenModalDelete && (
        <GeneralModal
          title={
            languageMap?.["modal.confirm.titleDeleteFiles"] ??
            "Do you want to delete the selected files?"
          }
          content={
            languageMap?.["modal.confirm.contentDeleteFiles"] ??
            "Once deleted, the files will be gone permanently."
          }
          open={isOpenModalDelete}
          onConfirm={deleteStorageFiles}
          onCancel={() => setIsOpenModalDelete(false)}
        />
      )}
    </div>
  );
};
const Storage = () => {
  const {
    storageSearch,
    setStorageSearch,
    pagination,
    resetPagination,
    viewMore,
    onChangeSearch,
    isLoading,
    isLoadingMore,
    hasMore,
    onChangeDateType,
    mediaTypes,
  } = useStorageContext();
  const { languageMap } = useInfoUser();

  const scrollContainerRef = useRef(null);
  const observerRef = useRef(null);

  const sentinelRef = useCallback(
    (node) => {
      if (isLoading) return;
      if (observerRef.current) observerRef.current.disconnect();

      if (!node) return;

      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
            viewMore();
          }
        },
        {
          root: scrollContainerRef.current,
          rootMargin: "250px 0px",
          threshold: 0,
        }
      );

      observerRef.current.observe(node);
    },
    [isLoading, isLoadingMore, hasMore, viewMore]
  );

  return (
    <div className="flex flex-col h-full w-full overflow-hidden">
      {/* ===== Fixed Header Section ===== */}
      <div className="flex flex-col items-center flex-shrink-0 w-full pt-[20px] pb-[8px] px-[25px] bg-white border-b border-gray-100 z-10">
        <div className="text-[22px] font-semibold text-gray-800 mb-[12px]">
          {languageMap?.["storage.title.welcome"] ?? "Welcome to Storage"}
        </div>
        <Input
          placeholder={
            languageMap?.["storage.search.placeHolder"] ?? "Search in Storage"
          }
          className="w-[50%] max-w-[500px] h-[42px] rounded-[30px] text-[15px] mb-[10px]"
          prefix={<IoSearchOutline size={18} />}
          allowClear
          value={storageSearch?.fileName}
          onChange={onChangeSearch}
        />
        <div className="flex flex-wrap justify-center items-center gap-[10px]">
          <Select
            options={mediaTypes}
            className="w-[170px] rounded-[30px]"
            placeholder={
              languageMap?.["storage.combobox.mediaType"] ?? "Media type"
            }
            value={storageSearch?.mediaType}
            allowClear
            showSearch
            filterOption={(input, option) => {
              const searchText =
                option?.title ||
                (typeof option?.label === "string" ? option?.label : "") ||
                option?.value ||
                "";
              return searchText.toLowerCase().includes(input.toLowerCase());
            }}
            onChange={(value) => {
              resetPagination();
              setStorageSearch((prev) => ({
                ...prev,
                mediaType: value,
              }));
            }}
          />
          <Select
            allowClear
            options={locationTypeList}
            className="w-[140px] rounded-[30px]"
            placeholder={
              languageMap?.["storage.combobox.locationType"] ?? "Location type"
            }
            value={storageSearch?.conversationType}
            onChange={(value) => {
              resetPagination();
              setStorageSearch((prev) => ({
                ...prev,
                conversationType: value,
              }));
            }}
          />
          <Select
            allowClear
            options={dateTypeList}
            className="w-[140px] rounded-[30px]"
            placeholder={
              languageMap?.["storage.combobox.dateType"] ?? "Date type"
            }
            value={storageSearch?.dateType}
            onChange={onChangeDateType}
          />

          {storageSearch?.dateType === "DATE" && (
            <DatePicker
              needConfirm={false}
              format="YYYY-MM-DD"
              className="w-[140px]"
              value={storageSearch?.selectedDate}
              onChange={(value) => {
                resetPagination();
                setStorageSearch((prev) => ({
                  ...prev,
                  selectedDate: value,
                }));
              }}
            />
          )}
          {storageSearch?.dateType === "RANGE" && (
            <RangePicker
              className="h-[32px]"
              onChange={(value) => {
                resetPagination();
                setStorageSearch((prev) => ({
                  ...prev,
                  fromDate: value?.[0] || null,
                  toDate: value?.[1] || null,
                }));
              }}
            />
          )}
        </div>
        {!isLoading ? (
          <div className="text-[13px] text-gray-500 pt-[8px] pb-[2px]">
            {Math.min(
              pagination?.page * STORAGE.STORAGE_LIMIT,
              pagination?.total
            )}
            /{pagination?.total}
          </div>
        ) : (
          <Spin className="h-[20px] pt-[8px] pb-[2px]" />
        )}
      </div>

      {/* ===== Scrollable Content Section ===== */}
      <div
        ref={scrollContainerRef}
        className="flex-1 w-full overflow-y-auto overflow-x-hidden pt-[15px]"
      >
        <StorageList />
        {/* Sentinel trigger for infinite scroll */}
        <div
          ref={sentinelRef}
          className="w-full flex justify-center items-center py-6 min-h-[48px]"
        >
          {isLoadingMore ? (
            <Spin />
          ) : !hasMore && pagination?.total > 0 ? (
            <span className="text-gray-400 text-xs">
              {languageMap?.["storage.allLoaded"] ?? "All files loaded"}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export { Storage };
